import React, { useEffect, useReducer, useState } from "react";
import { 
    Box, Button, Container, Grid, Typography, Paper, Stepper, Step, StepLabel, 
    Card, CardActionArea, Stack, TextField, MenuItem, FormHelperText,
    Fade, Switch, FormControlLabel
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

// ICONOS
import Inventory2Icon from '@mui/icons-material/Inventory2';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import CardMembershipIcon from '@mui/icons-material/CardMembership';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

// COMPONENTES Y CONTROLADORES
import { getForStoreData, createProduct } from "../../../../controllers/company/products/ProductsMerchantController";
import { useHistory } from "react-router-dom";
import { Dropzone, FileMosaic, FullScreen, ImagePreview } from "@files-ui/react";
import RichTextEditor from "../../../../components/RichTextEditor";
import CustomBreadcrumbs from "../../../../components/breadcrumbs";
import QUDProgress from "../../../../components/progress";
import ResponseComponent from "../../../../components/responseComponent";

// TUS COMPONENTES DE VARIANTES
import GroupedAttributeValueSelector from "../../../../components/merchants/products/GroupedAttributeValueSelector";
import VariantList from "../../../../components/merchants/products/VariantList";

// --- ESTADO INICIAL ---
const initialState = {
    // Data maestra
    unidades:[], categorias:[], attributes:[], allModifierGroups:[], plans:[],
    
    // Campos
    nombre:'', descripcion:'', idUnidad:'', idSubCategoria:'', estatus:'1',
    precio:'', cantidad:'',
    coverFile: [],
    
    // Flags
    productType: '', 
    is_subscription: false,
    is_reservable: false,
    has_variants: false,

    // Específicos
    variants: [],
    plan_id: '',
    duration_minutes: '',
    
    // Feedback
    isLoading: false, open: false, title: '', message: '', success: false
}

function reducer(state, action) {
    return { ...state, ...action };
}

export default function ProductWizard() {
    const [state, setState] = useReducer(reducer, initialState);
    const [activeStep, setActiveStep] = useState(0);
    
    const [imgPreviewSrc, setImgPreviewSrc] = useState(undefined);
    
    // Estado local para los IDs de atributos seleccionados
    const [selectedValueIds, setSelectedValueIds] = useState([]); 

    const [errors, setErrors] = useState({});
    const history = useHistory();

    useEffect(() => {
        loadMasterData();
    }, []);

    const loadMasterData = async () => {
        const response = await getForStoreData();
        if(response.success) {
            setState({
                unidades: response.data.unidades,
                categorias: response.data.categorias,
                attributes: response.data.attributes,
                allModifierGroups: response.data.modifier_groups,
                plans: response.data.plans
            });
        }
    };

    const handleChange = (e) => setState({ [e.target.name]: e.target.value });

    // --- LÓGICA DE VARIANTES (RESTAURADA) ---
    
    const handleGenerateVariants = () => {
        // 1. Agrupar valores por atributo padre
        const groupedValues = selectedValueIds.reduce((acc, valueId) => {
            for (const attribute of state.attributes) {
                const foundValue = attribute.attribute_values.find(v => v.id === valueId);
                if (foundValue) {
                    if (!acc[attribute.name]) acc[attribute.name] = [];
                    acc[attribute.name].push(foundValue);
                    break; 
                }
            }
            return acc;
        }, {});

        // 2. Producto Cartesiano
        const valueArrays = Object.values(groupedValues);
        if (valueArrays.length === 0) {
            setState({ variants: [] });
            return;
        }
        
        const combinations = valueArrays.reduce((acc, currentValues) => {
            if (acc.length === 0) return currentValues.map(v => [v]);
            return acc.flatMap(existingCombination =>
                currentValues.map(newValue => [...existingCombination, newValue])
            );
        }, []);

        // 3. Crear estructura para VariantList
        const newVariants = combinations.map((combination) => {
            const key = combination.map(v => v.id).join('-');
            return {
                key: key,
                price: state.precio || '', // Hereda precio base si existe
                quantity: state.cantidad || '', 
                sku: '',
                combination: combination,
                attribute_values: combination.map(v => v.id),
                errorPrice: false, 
                errorQuantity: false
            };
        });

        setState({ variants: newVariants });
    };

    const handleVariantChange = (key, field, value) => {
        const updatedVariants = state.variants.map(variant => {
            if (variant.key === key) {
                // Si escriben, limpiamos el error específico
                if (field === 'price') return { ...variant, [field]: value, errorPrice: false };
                if (field === 'quantity') return { ...variant, [field]: value, errorQuantity: false };
                return { ...variant, [field]: value };
            }
            return variant;
        });
        setState({ variants: updatedVariants });
    };

    // --- VALIDACIONES ---
    const validateStep = () => {
        let newErrors = {};
        let isValid = true;

        // Paso 1: Tipo
        if (activeStep === 0) {
            if (!state.productType) { isValid = false; alert("Selecciona un tipo de producto"); }
        }

        // Paso 2: Info Básica
        if (activeStep === 1) {
            if (!state.nombre) newErrors.nombre = true;
            if (!state.descripcion) newErrors.descripcion = true;
            if (!state.idSubCategoria) newErrors.idSubCategoria = true;
            if (state.coverFile.length === 0) newErrors.coverFile = true;
        }

        // Paso 3: Específicos
        if (activeStep === 2) {
            if (state.productType === 'subscription') {
                if (!state.plan_id) newErrors.plan_id = true;
            } 
            else if (state.productType === 'service') {
                if (!state.precio) newErrors.precio = true;
                if (!state.duration_minutes) newErrors.duration_minutes = true;
            } 
            else if (state.productType === 'physical') {
                if (state.has_variants) {
                    if (state.variants.length === 0) { isValid = false; alert("Debes generar al menos una variante"); }
                    
                    // Validar cada variante individualmente
                    const validatedVariants = state.variants.map(v => {
                        let errP = false, errQ = false;
                        if (!v.price || v.price < 0) { isValid = false; errP = true; }
                        if (!v.quantity || v.quantity < 0) { isValid = false; errQ = true; }
                        return { ...v, errorPrice: errP, errorQuantity: errQ };
                    });
                    
                    if (!isValid) setState({ variants: validatedVariants }); // Actualizamos UI con errores
                } else {
                    if (!state.precio) newErrors.precio = true;
                    if (!state.cantidad) newErrors.cantidad = true;
                }
            }
        }

        if (Object.keys(newErrors).length > 0) isValid = false;
        setErrors(newErrors);
        return isValid;
    };

    // --- NAVEGACIÓN ---
    const handleNext = () => {
        if (validateStep()) {
            if (activeStep === 2) handleSubmit();
            else setActiveStep((prev) => prev + 1);
        }
    };
    const handleBack = () => setActiveStep((prev) => prev - 1);
    
    const handleTypeSelect = (type) => {
        setState({
            productType: type,
            is_subscription: type === 'subscription',
            is_reservable: type === 'service',
            has_variants: false, variants: [], plan_id: '', duration_minutes: ''
        });
    };

    const handleSubmit = async () => {
        setState({ isLoading: true });
        const response = await createProduct(
            state.nombre, state.descripcion, state.idUnidad || 1, state.idSubCategoria, state.estatus,
            state.coverFile[0]?.file, state.has_variants, state.variants,
            state.precio, state.cantidad, [], 
            state.is_subscription, state.plan_id,
            state.is_reservable, state.duration_minutes
        );
        setState({
            isLoading: false, open: true, success: response.success,
            title: response.success ? 'Éxito' : 'Error', message: response.message
        });
    };

    // --- RENDERIZADO POR PASOS ---

    const renderStep0 = () => (
        <Grid container spacing={3} justifyContent="center" sx={{ mt: 2 }}>
            {[
                { id: 'physical', label: 'Producto Físico', icon: <Inventory2Icon fontSize="large" />, desc: 'Inventario, variantes y envío.' },
                { id: 'service', label: 'Servicio / Reserva', icon: <EventAvailableIcon fontSize="large" />, desc: 'Agenda, canchas y turnos.' },
                { id: 'subscription', label: 'Suscripción', icon: <CardMembershipIcon fontSize="large" />, desc: 'Cobros recurrentes (planes).' }
            ].map((type) => (
                <Grid item xs={12} md={4} key={type.id}>
                    <Card variant="outlined" sx={{ 
                        height: '100%', border: state.productType === type.id ? '2px solid #1976d2' : '1px solid #e0e0e0',
                        bgcolor: state.productType === type.id ? '#f0f7ff' : 'white'
                    }}>
                        <CardActionArea onClick={() => handleTypeSelect(type.id)} sx={{ height: '100%', p: 3, textAlign: 'center' }}>
                            <Box sx={{ color: state.productType === type.id ? 'primary.main' : 'text.secondary', mb: 2 }}>{type.icon}</Box>
                            <Typography variant="h6">{type.label}</Typography>
                            <Typography variant="body2" color="text.secondary">{type.desc}</Typography>
                            {state.productType === type.id && <CheckCircleIcon color="primary" sx={{ position: 'absolute', top: 10, right: 10 }} />}
                        </CardActionArea>
                    </Card>
                </Grid>
            ))}
        </Grid>
    );

    const renderStep1 = () => (
        <Grid container spacing={4} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
                 <Typography variant="h6" gutterBottom>Info General</Typography>
                 <Stack spacing={3}>
                    <TextField label="Nombre" name="nombre" fullWidth required value={state.nombre} onChange={handleChange} error={errors.nombre} />
                    <TextField select label="Categoría" name="idSubCategoria" fullWidth required value={state.idSubCategoria} onChange={handleChange} error={errors.idSubCategoria}>
                        {state.categorias.map(c => <MenuItem key={c.idSubCategoria} value={c.idSubCategoria}>{c.nombre}</MenuItem>)}
                    </TextField>
                    <Box>
                        <Typography variant="caption" color="text.secondary">Descripción</Typography>
                        <RichTextEditor value={state.descripcion} onChange={(val) => setState({descripcion: val})} />
                        {errors.descripcion && <FormHelperText error>Requerido</FormHelperText>}
                    </Box>
                 </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
                <Typography variant="h6" gutterBottom>Foto Principal</Typography>
                <Dropzone onChange={(files) => setState({coverFile: files})} value={state.coverFile} maxFiles={1} accept="image/*" label="Arrastra imagen aquí" style={{ minHeight: 200, border: errors.coverFile ? '1px dashed red' : '1px dashed #ccc' }}>
                    {state.coverFile.map((file) => <FileMosaic key={file.id} {...file} onSee={setImgPreviewSrc} onDelete={() => setState({coverFile: []})} preview />)}
                </Dropzone>
            </Grid>
        </Grid>
    );

    const renderStep2 = () => (
        <Box sx={{ mt: 2, maxWidth: 900, mx: 'auto' }}>
            
            {/* TIPO: SUSCRIPCIÓN */}
            {state.productType === 'subscription' && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                    <Typography variant="h6" gutterBottom>Plan de Facturación</Typography>
                    <TextField select label="Plan Asociado" name="plan_id" fullWidth value={state.plan_id} onChange={handleChange} error={errors.plan_id} helperText="Selecciona el plan recurrente">
                        {state.plans.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
                    </TextField>
                </Paper>
            )}

            {/* TIPO: SERVICIO */}
            {state.productType === 'service' && (
                <Paper variant="outlined" sx={{ p: 4 }}>
                    <Typography variant="h6" gutterBottom>Datos de la Reserva</Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Precio ($)" name="precio" type="number" fullWidth required value={state.precio} onChange={handleChange} error={errors.precio} />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <TextField label="Duración (Minutos)" name="duration_minutes" type="number" fullWidth required value={state.duration_minutes} onChange={handleChange} error={errors.duration_minutes} helperText="Tiempo por turno." />
                        </Grid>
                    </Grid>
                </Paper>
            )}

            {/* TIPO: FÍSICO (CON VARIANTES) */}
            {state.productType === 'physical' && (
                <Paper variant="outlined" sx={{ p: 3 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                        <Typography variant="h6">Inventario</Typography>
                        <FormControlLabel
                            control={<Switch checked={state.has_variants} onChange={(e) => setState({ has_variants: e.target.checked })} />}
                            label="Tiene variantes (Tallas/Colores)"
                        />
                    </Stack>

                    {!state.has_variants ? (
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <TextField label="Precio ($)" name="precio" type="number" fullWidth value={state.precio} onChange={handleChange} error={errors.precio} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField label="Stock" name="cantidad" type="number" fullWidth value={state.cantidad} onChange={handleChange} error={errors.cantidad} />
                            </Grid>
                        </Grid>
                    ) : (
                        <Box>
                            <Box sx={{ p: 2, bgcolor: '#f9f9f9', borderRadius: 2, mb: 3 }}>
                                <Typography variant="subtitle2" gutterBottom>1. Selecciona las opciones:</Typography>
                                <GroupedAttributeValueSelector 
                                    attributes={state.attributes} 
                                    onChange={setSelectedValueIds} 
                                />
                                <Button variant="contained" onClick={handleGenerateVariants} sx={{ mt: 2 }}>
                                    Generar Combinaciones
                                </Button>
                            </Box>

                            {state.variants.length > 0 && (
                                <Box>
                                    <Typography variant="subtitle2" gutterBottom>2. Define precios y stock:</Typography>
                                    {/* AQUI ESTÁ TU COMPONENTE ORIGINAL INTEGRADO */}
                                    <VariantList 
                                        variants={state.variants} 
                                        onVariantChange={handleVariantChange} 
                                    />
                                </Box>
                            )}
                        </Box>
                    )}
                </Paper>
            )}
        </Box>
    );

    return (
        <Container maxWidth="lg" sx={{ pb: 8 }}>
            <CustomBreadcrumbs returnTo='/products' crumbs={[{name:'Productos',link:'/products'}, {name:'Nuevo',link:'#'}]} />
            
            <Box sx={{ width: '100%', mt: 4, mb: 6 }}>
                <Stepper activeStep={activeStep} alternativeLabel>
                    {['Tipo', 'Básico', 'Detalles'].map((label) => <Step key={label}><StepLabel>{label}</StepLabel></Step>)}
                </Stepper>
            </Box>

            <Box sx={{ minHeight: 400 }}>
                {activeStep === 0 && renderStep0()}
                {activeStep === 1 && renderStep1()}
                {activeStep === 2 && renderStep2()}
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6, pt: 3, borderTop: '1px solid #eee' }}>
                <Button color="inherit" disabled={activeStep === 0} onClick={handleBack} startIcon={<ArrowBackIcon />}>Atrás</Button>
                <LoadingButton variant="contained" onClick={handleNext} loading={state.isLoading} endIcon={activeStep === 2 ? <CheckCircleIcon /> : <ArrowForwardIcon />}>
                    {activeStep === 2 ? 'Crear Producto' : 'Siguiente'}
                </LoadingButton>
            </Box>

            <QUDProgress open={state.isLoading} />
            <ResponseComponent show={state.open} success={state.success} title={state.title} message={state.message} actionText="Finalizar" onClick={() => state.success ? history.push('/products') : setState({open: false})} />
            <FullScreen open={imgPreviewSrc !== undefined} onClose={() => setImgPreviewSrc(undefined)}><ImagePreview src={imgPreviewSrc} /></FullScreen>
        </Container>
    );
}